<template>
  <div
    class="guide-tooltip"
    :class="[`guide-tooltip--${placement}`, `guide-tooltip--${importance}`]"
    :style="tooltipStyle"
  >
    <!-- Arrow (rotated square) -->
    <span class="guide-tooltip__arrow" :style="arrowStyle" aria-hidden="true"></span>

    <!-- Header: importance chip + step counter -->
    <div class="guide-tooltip__header">
      <span class="guide-tooltip__chip">
        <span class="guide-tooltip__chip-dot" aria-hidden="true"></span>
        {{ importanceLabel }}
      </span>
      <span v-if="stepInfo.hasMultipleSteps" class="guide-tooltip__counter">
        {{ stepInfo.current }}/{{ stepInfo.total }}
      </span>
    </div>

    <p class="guide-tooltip__text">{{ text }}</p>

    <!-- Footer: progress dots + actions -->
    <div class="guide-tooltip__footer">
      <div v-if="stepInfo.hasMultipleSteps" class="guide-tooltip__dots" aria-hidden="true">
        <span
          v-for="i in stepInfo.total"
          :key="i"
          class="guide-tooltip__dot"
          :class="{ active: i === stepInfo.current, done: i < stepInfo.current }"
        ></span>
      </div>

      <div class="guide-tooltip__actions">
        <button
          v-if="stepInfo.hasMultipleSteps"
          type="button"
          class="guide-tooltip__btn guide-tooltip__btn--ghost"
          @click="$emit('skipAll')"
        >
          דלג על הכל
        </button>
        <button
          type="button"
          class="guide-tooltip__btn guide-tooltip__btn--primary"
          @click="$emit('next')"
        >
          {{ nextLabel }}
          <svg
            v-if="!stepInfo.isLast"
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { IMPORTANCE_LEVELS } from '../useTutorial'

const props = defineProps({
  text: {
    type: String,
    required: true,
  },
  importance: {
    type: String,
    default: 'info',
    validator: (val) => ['info', 'important', 'critical'].includes(val),
  },
  placement: {
    type: String,
    default: 'bottom',
    validator: (val) => ['top', 'bottom', 'start', 'end'].includes(val),
  },
  position: {
    type: Object,
    default: () => ({ top: 0, left: 0, arrowPosition: 50, maxWidth: 320 }),
  },
  stepInfo: {
    type: Object,
    default: () => ({ current: 1, total: 1, isFirst: true, isLast: true, hasMultipleSteps: false }),
  },
})

defineEmits(['next', 'skipAll'])

const importanceConfig = computed(
  () => IMPORTANCE_LEVELS[props.importance] || IMPORTANCE_LEVELS.info
)
const importanceLabel = computed(() => importanceConfig.value.label)

const nextLabel = computed(() => {
  if (!props.stepInfo.hasMultipleSteps) return 'הבנתי'
  return props.stepInfo.isLast ? 'סיימתי' : 'הבא'
})

const tooltipStyle = computed(() => ({
  top: `${props.position.top}px`,
  left: `${props.position.left}px`,
  maxWidth: `${props.position.maxWidth}px`,
  '--importance-color': importanceConfig.value.color,
  '--importance-bg': importanceConfig.value.bgColor,
}))

const arrowStyle = computed(() => {
  const arrowPos = props.position.arrowPosition || 50
  if (props.placement === 'top' || props.placement === 'bottom') {
    return { right: `calc(${arrowPos}% - 7px)` }
  }
  return { top: 'calc(50% - 7px)' }
})
</script>

<style scoped>
.guide-tooltip {
  position: fixed;
  z-index: 10002;
  direction: rtl;
  background: var(--bg-primary, #ffffff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: var(--border-radius-lg, 16px);
  box-shadow:
    0 20px 40px -8px rgb(15 23 42 / 0.25),
    0 8px 16px -4px rgb(15 23 42 / 0.1);
  padding: 14px 16px;
  font-family: var(--font-family, 'Rubik', sans-serif);
}

/* Arrow: rotated square sharing the card's background + border */
.guide-tooltip__arrow {
  position: absolute;
  width: 14px;
  height: 14px;
  background: inherit;
  border: inherit;
  transform: rotate(45deg);
}

.guide-tooltip--bottom .guide-tooltip__arrow {
  top: -8px;
  border-bottom: none;
  border-left: none;
}

.guide-tooltip--top .guide-tooltip__arrow {
  bottom: -8px;
  border-top: none;
  border-right: none;
}

.guide-tooltip--start .guide-tooltip__arrow {
  left: -8px;
  border-top: none;
  border-left: none;
}

.guide-tooltip--end .guide-tooltip__arrow {
  right: -8px;
  border-bottom: none;
  border-right: none;
}

/* ---------- Header ---------- */
.guide-tooltip__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm, 8px);
  margin-bottom: 8px;
}

.guide-tooltip__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: var(--border-radius-full, 9999px);
  background: var(--importance-bg);
  color: var(--importance-color);
  font-size: var(--font-size-xs, 0.75rem);
  font-weight: var(--font-weight-semibold, 600);
}

.guide-tooltip__chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.guide-tooltip__counter {
  color: var(--text-muted, #94a3b8);
  font-size: var(--font-size-xs, 0.75rem);
  font-weight: var(--font-weight-medium, 500);
  font-variant-numeric: tabular-nums;
}

/* ---------- Text ---------- */
.guide-tooltip__text {
  margin: 0 0 12px;
  color: var(--text-primary, #0f172a);
  font-size: var(--font-size-sm, 0.875rem);
  line-height: 1.6;
}

/* ---------- Footer ---------- */
.guide-tooltip__footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.guide-tooltip__dots {
  display: flex;
  align-items: center;
  gap: 5px;
}

.guide-tooltip__dot {
  width: 6px;
  height: 6px;
  border-radius: var(--border-radius-full, 9999px);
  background: var(--color-gray-200, #e2e8f0);
  transition: all var(--transition-base, 200ms);
}

.guide-tooltip__dot.done {
  background: var(--color-primary-light, #475569);
}

.guide-tooltip__dot.active {
  width: 18px;
  background: var(--color-primary, #283548);
}

.guide-tooltip__actions {
  display: flex;
  gap: 8px;
}

.guide-tooltip__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 38px;
  padding: 8px 16px;
  border-radius: var(--border-radius, 10px);
  font-family: inherit;
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--font-weight-semibold, 600);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms);
  -webkit-tap-highlight-color: transparent;
}

.guide-tooltip__btn--primary {
  flex: 1;
  border: none;
  background: var(--gradient-primary, linear-gradient(135deg, #1e293b, #334155));
  color: var(--color-white, #fff);
  box-shadow: 0 2px 8px rgba(30, 41, 59, 0.3);
}

.guide-tooltip__btn--primary:hover {
  filter: brightness(1.15);
  box-shadow: 0 4px 12px rgba(30, 41, 59, 0.4);
}

.guide-tooltip__btn--primary:active {
  transform: scale(0.98);
}

.guide-tooltip__btn--ghost {
  border: 1px solid var(--border-color, #e2e8f0);
  background: transparent;
  color: var(--text-secondary, #475569);
}

.guide-tooltip__btn--ghost:hover {
  background: var(--bg-secondary, #f8fafc);
  border-color: var(--color-gray-300, #cbd5e1);
}

/* ---------- Touch devices ---------- */
@media (max-width: 640px), (pointer: coarse) {
  .guide-tooltip__btn {
    min-height: 44px;
    padding: 10px 18px;
  }
}
</style>
